/**
 * Constants for controlling MessageInput component sections visibility
 */

export const MessageInputControl = {
  /** Agent selector button */
  AGENT_SELECTOR: "agent_selector",
  /** File upload functionality */
  FILE_UPLOAD: "file_upload",
  /** Entity selector for connections and references */
  ENTITY_SELECTOR: "entity_selector",
  /** Skills button (/) */
  SKILLS: "skills",
  /** Suggested actions menu button */
  SUGGESTED_ACTIONS: "suggested_actions",
  /** Placeholder insertion button ({}) */
  PLACEHOLDERS: "placeholders",
  /** Audio recording/microphone button */
  MICROPHONE: "microphone",
  /** Send button */
  SEND_BUTTON: "send_button",
  /** Agent reasoning effort selector (canvas context only) */
  REASONING_EFFORT: "reasoning_effort",
  /** Confirmation-mode selector (manual vs. bypass permissions) */
  CONFIRMATION_MODE: "confirmation_mode",
} as const;

export const MessageInputControlSet = {
  /** Default visible sections for MessageInput */
  DEFAULT: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.SUGGESTED_ACTIONS,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
  ],
  /** Home / workspace hero composer — the default controls plus the
   *  confirmation-mode selector. Session-less until send, so its
   *  confirmation mode is a draft the composer applies to the flow session
   *  once one is minted. */
  HOME_COMPOSER: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.CONFIRMATION_MODE,
    MessageInputControl.SUGGESTED_ACTIONS,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
  ],
  /** Session side pane — the default chat controls plus the
   *  confirmation-mode selector. */
  SIDECHAT: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.CONFIRMATION_MODE,
    MessageInputControl.SUGGESTED_ACTIONS,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
  ],
  /** Canvas session pane — file upload (lands as `class=canvas` and on
   *  the canvas as a node), skills, confirmation mode, add context
   *  (connections), reasoning effort, mic, send. */
  CANVAS_CHAT: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.CONFIRMATION_MODE,
    MessageInputControl.REASONING_EFFORT,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
  ],
  /** Full-page workspace chat — file upload, skills, entity selector,
   *  confirmation mode, reasoning effort, mic, send. No agent selector. */
  WORKSPACE_CHAT: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.CONFIRMATION_MODE,
    MessageInputControl.REASONING_EFFORT,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
  ],
  /** Floating canvas quick chat — same controls as CANVAS_CHAT, including
   *  the confirmation-mode selector. */
  CANVAS_QUICK_CHAT: [
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.CONFIRMATION_MODE,
    MessageInputControl.REASONING_EFFORT,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
  ],
  /** For editing suggested actions - minimal with placeholders only */
  SUGGESTED_ACTIONS_EDITOR: [
    MessageInputControl.SKILLS,
    MessageInputControl.PLACEHOLDERS,
    MessageInputControl.SEND_BUTTON,
  ],
  FULL: [
    MessageInputControl.AGENT_SELECTOR,
    MessageInputControl.FILE_UPLOAD,
    MessageInputControl.ENTITY_SELECTOR,
    MessageInputControl.SKILLS,
    MessageInputControl.SUGGESTED_ACTIONS,
    MessageInputControl.PLACEHOLDERS,
    MessageInputControl.MICROPHONE,
    MessageInputControl.SEND_BUTTON,
  ],
  SEND_ONLY: [],
} as const;

export type MessageInputControl =
  (typeof MessageInputControl)[keyof typeof MessageInputControl];

export type MessageInputControlSet = readonly MessageInputControl[];

/**
 * Utility functions for working with MessageInput sections
 */
export const MessageInputControlUtils = {
  /**
   * Check if a specific section is visible
   */
  isVisible: (
    sections: MessageInputControlSet,
    section: MessageInputControl,
  ): boolean => sections.includes(section),
};

/**
 * Usage Examples:
 *
 * ```tsx
 * import { MessageInputControl, MessageInputControlSet } from "@/constants/message-input";
 *
 * // Default configuration (file upload, entity selector, skills, suggestions, microphone)
 * <MessageInput controlSet={MessageInputControlSet.DEFAULT} />
 *
 * // Full-featured input (all controls)
 * <MessageInput controlSet={MessageInputControlSet.FULL} />
 *
 * // Custom: Agent selector + entity selector + microphone
 * <MessageInput
 *   controlSet={[
 *     MessageInputControl.AGENT_SELECTOR,
 *     MessageInputControl.ENTITY_SELECTOR,
 *     MessageInputControl.MICROPHONE
 *   ]}
 * />
 *
 * // Completely hidden toolbar (just text input and send button)
 * <MessageInput controlSet={[]} />
 *
 * // Agent-focused input
 * <MessageInput
 *   controlSet={[
 *     MessageInputControl.AGENT_SELECTOR,
 *     MessageInputControl.SKILLS,
 *     MessageInputControl.MICROPHONE
 *   ]}
 * />
 * ```
 */
